import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import EmployeeSelector from 'modules/core/components/EmployeeSelector'
import type { FilterProps } from 'modules/core/components/Filters/types'
import Loading from 'modules/core/components/Loading'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import { usePolicyAssignmentEligibleEmployees } from 'modules/payroll_bundle/hooks/usePolicyAssignmentEligibleEmployees'

interface Props {
  onChange: (value: number[]) => void
  country: string
  currency: string
  selectedEmployeeIds: number[]
  disabledIneligibleEmployees?: boolean
  filterProps?: FilterProps
  contractVersions: ContractVersions
}

const EmployeesSelector = ({
  onChange,
  country,
  currency,
  selectedEmployeeIds,
  filterProps = {
    hideFilterByStatus: false,
    showFilterByTeam: true,
  },
  contractVersions,
}: Props) => {
  const [activeEmployeesList, setActiveEmployeesList] = React.useState<Employee[]>([])

  const eligibleEmployeeIds: number[] = useMemo(() => {
    const eligibleEmployees = employees.filter((employee) => {
      const isActiveOnPolicy = !employee.isTerminated

      const legalEntity = employeeLegalEntity(employee)
      const matchesCurrencyAndCountry =
        legalEntity.attributes.currency === currency && legalEntity.attributes.country === country

      if (currency !== null && country !== null) {
        return isActiveOnPolicy && matchesCurrencyAndCountry
      } else {
        return isActiveOnPolicy
      }
    })
    return eligibleEmployees.map((employee) => employee.id)
  }, [country, currency])

  const { fetching, policyAssignmentEligibleEmployeeIds } =
    usePolicyAssignmentEligibleEmployees(eligibleEmployeeIds)

  // Employees with historical data are not eligible to be assigned to a policy. In order to know more about how we
  // determine if an employee has historical data, you can check the repository PayrollPolicyAssignmentEligibleEmployees.
  const ineligibleEmployeeIds: number[] = policyAssignmentEligibleEmployeeIds
    ? eligibleEmployeeIds.filter(
        (employeeId) => !policyAssignmentEligibleEmployeeIds.includes(employeeId)
      )
    : eligibleEmployeeIds

  const onSelectEmployee = (employeeId: number | null | undefined) => {
    if (!employeeId) {
      onChange([])
      return
    }

    const employeeAlreadySelected = !!selectedEmployeeIds.includes(employeeId)

    if (employeeAlreadySelected) {
      onChange(selectedEmployeeIds.filter((id) => id !== employeeId))
    } else {
      const employeesSelected = [...(selectedEmployeeIds ?? [])]
      employeesSelected?.push(employeeId)

      onChange(employeesSelected)
    }
  }

  const onSelectEmployees = (ids: number[]) => {
    onChange(ids)
  }

  useUnsafeEffect(() => {
    const today = moment()

    const activePolicyContracts = contractVersions.filter((contract) =>
      contract.activeWithinDateRange(today, today)
    )

    const policyEmployeeIdsWithActiveContract = new Set(
      activePolicyContracts.map((contract) => contract.attributes.employee_id)
    )

    const activeEmployees = employees.filter((employee) => {
      const employeeHasActiveContract =
        policyEmployeeIdsWithActiveContract.has(employee.id) &&
        employee.activeWithinDateRange(today, today)

      const legalEntity = employeeLegalEntity(employee)
      const employeeHasCorrectCurrencyAndCountry =
        country === legalEntity.country && currency === legalEntity.currency

      return employeeHasCorrectCurrencyAndCountry && employeeHasActiveContract
    })

    setActiveEmployeesList(activeEmployees)
  }, [country, currency])

  const dimEmployeeSelector = !country.length || !policyAssignmentEligibleEmployeeIds

  return (
    <Box position="relative">
      {/* Dimming since our selectors don't support a loading decorator */}
      <Box
        style={{
          opacity: dimEmployeeSelector ? '0.5 ' : '1',
        }}
      >
        <EmployeeSelector
          employees={activeEmployeesList}
          onSelectEmployee={onSelectEmployee}
          selectedEmployeeIds={selectedEmployeeIds || []}
          onSelectMultiple={onSelectEmployees}
          disabledEmployeeIds={ineligibleEmployeeIds}
          filterProps={filterProps}
        />
      </Box>
      {fetching && (
        <Box
          height="full"
          width="full"
          position="absolute"
          justifyContent="center"
          alignItems="center"
        >
          <Loading />
        </Box>
      )}
    </Box>
  )
}

export default observer(EmployeesSelector)
